import { BottomSheet } from '@/components/BottomSheet';
import { Input } from '@/components/Input';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { classNames } from '@/utils/classNames';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { CalendarIcon, ChevronDownIcon } from '@/components/icons';
import styles from './DatePicker.module.css';

interface DatePickerProps {
  id?: string;
  value: string;
  onChange: (date: string) => void;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  defaultViewDate?: Date;
  onValidationChange?: (isValid: boolean) => void;
  dropdownPosition?: 'above' | 'below';
  /** When set, returning false makes the date invalid (in addition to min/max) and blocks it in the calendar. */
  isDateAllowed?: (date: Date) => boolean;
  disallowedSelectionMessage?: string;
  /** Shown under the month grid: end date of the merged season that overlaps this month (null in “gap” months). */
  getSeasonCalendarFooterText?: (year: number, monthIndex: number) => string | null;
  sheetTitle?: string;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const formatDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString();
  return `${year}-${month}-${day}`;
};

const getMinimumDate = (): Date => {
  const now = new Date();
  const minDateTime = new Date(now.getTime() + 48 * 60 * 60 * 1000);

  const minDate = new Date(
    minDateTime.getFullYear(),
    minDateTime.getMonth(),
    minDateTime.getDate()
  );

  if (minDateTime.getHours() > 0 || minDateTime.getMinutes() > 0 || minDateTime.getSeconds() > 0) {
    minDate.setDate(minDate.getDate() + 1);
  }

  return minDate;
};

const isDateBefore = (date: Date, compareDate: Date): boolean => {
  const d1 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const d2 = new Date(compareDate.getFullYear(), compareDate.getMonth(), compareDate.getDate());
  return d1 < d2;
};

const isDateAfter = (date: Date, compareDate: Date): boolean => {
  const d1 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const d2 = new Date(compareDate.getFullYear(), compareDate.getMonth(), compareDate.getDate());
  return d1 > d2;
};

const parseDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;

  const parts = dateStr.split('-');
  if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);

    if (isNaN(day) || isNaN(month) || isNaN(year)) {
      return null;
    }

    const date = new Date(year, month, day);

    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
      return null;
    }

    return date;
  }

  return null;
};

const isValidDateFormat = (dateStr: string): boolean => {
  if (!dateStr) return false;

  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(dateStr)) return false;

  const parts = dateStr.split('-');
  if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) return false;

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);

  if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  if (year < 1900 || year > 2100) return false;

  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

export const DatePicker = ({
  id,
  value,
  onChange,
  className,
  minDate,
  maxDate,
  defaultViewDate,
  onValidationChange,
  dropdownPosition = 'below',
  isDateAllowed,
  disallowedSelectionMessage,
  getSeasonCalendarFooterText,
  sheetTitle = 'Starting date',
}: DatePickerProps) => {
  const fallbackInputId = useId();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'month' | 'year'>('month');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(parseDate(value || ''));
  const [tempSelectedDate, setTempSelectedDate] = useState<Date | null>(parseDate(value || ''));
  const [hasError, setHasError] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [isInvalidFormat, setIsInvalidFormat] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputAreaRef = useRef<HTMLDivElement>(null);
  const calendarPanelRef = useRef<HTMLDivElement>(null);

  const minimumDate = useMemo(() => minDate || getMinimumDate(), [minDate]);

  const minYear = minimumDate.getFullYear();
  const maxYear = maxDate != null ? maxDate.getFullYear() : minYear + 20;

  const [yearChunkStart, setYearChunkStart] = useState(minYear);

  const yearChunk = useMemo(() => {
    const years: number[] = [];
    for (let y = yearChunkStart; y <= maxYear && years.length < 12; y++) {
      years.push(y);
    }
    return years;
  }, [yearChunkStart, maxYear]);

  const handlePrevYearChunk = () => {
    const newStart = Math.max(minYear, yearChunkStart - 12);
    setYearChunkStart(newStart);
  };

  const firstYearInChunk = yearChunk[0];
  const lastYearInChunk = yearChunk[yearChunk.length - 1];

  const handleNextYearChunk = () => {
    if (lastYearInChunk != null && lastYearInChunk < maxYear) {
      setYearChunkStart(yearChunkStart + 12);
    }
  };

  const handleYearSelect = (year: number) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
    setViewMode('month');
  };

  const openYearView = () => {
    const currentYear = currentMonth.getFullYear();
    setYearChunkStart(Math.max(minYear, Math.floor(currentYear / 12) * 12));
    setViewMode('year');
  };

  useEffect(() => {
    setInputValue(value || '');
    const parsedDate = parseDate(value || '');
    setSelectedDate(parsedDate);
    setTempSelectedDate(parsedDate);

    if (value && !parsedDate) {
      setIsInvalidFormat(true);
      setHasError(true);
      if (onValidationChange) {
        onValidationChange(false);
      }
    } else if (parsedDate) {
      setIsInvalidFormat(false);
      setCurrentMonth(parsedDate);

      const tooEarly = isDateBefore(parsedDate, minimumDate);
      const tooLate = maxDate != null && isDateAfter(parsedDate, maxDate);
      const allowedByRule = !isDateAllowed || isDateAllowed(parsedDate);
      const isValid = !tooEarly && !tooLate && allowedByRule;
      setHasError(!isValid);
      if (onValidationChange) {
        onValidationChange(isValid);
      }
    } else {
      setIsInvalidFormat(false);
      setHasError(false);
      if (defaultViewDate) {
        setCurrentMonth(defaultViewDate);
      }
      if (onValidationChange) {
        onValidationChange(true);
      }
    }
  }, [value, minimumDate, maxDate, defaultViewDate, onValidationChange, isDateAllowed]);

  useEffect(() => {
    if (isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setViewMode('month');
        setTempSelectedDate(selectedDate);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isMobile, isOpen, selectedDate]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: Array<{ date: number; isCurrentMonth: boolean }> = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      const prevMonthLastDay = new Date(year, month, 0).getDate();
      days.push({
        date: prevMonthLastDay - startingDayOfWeek + i + 1,
        isCurrentMonth: false,
      });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: i, isCurrentMonth: true });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: i, isCurrentMonth: false });
    }

    return days;
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const applyDate = (date: Date) => {
    if (isDateBefore(date, minimumDate)) {
      setHasError(true);
      setIsInvalidFormat(false);
      onValidationChange?.(false);
      setIsOpen(false);
      return;
    }
    if (maxDate != null && isDateAfter(date, maxDate)) {
      setHasError(true);
      setIsInvalidFormat(false);
      onValidationChange?.(false);
      setIsOpen(false);
      return;
    }
    if (isDateAllowed && !isDateAllowed(date)) {
      setHasError(true);
      setIsInvalidFormat(false);
      onValidationChange?.(false);
      return;
    }

    setSelectedDate(date);
    setTempSelectedDate(date);
    const formattedDate = formatDate(date);
    setInputValue(formattedDate);
    onChange(formattedDate);
    setHasError(false);
    setIsInvalidFormat(false);
    onValidationChange?.(true);
    setViewMode('month');
    setIsOpen(false);
  };

  const handleDateClick = (day: number) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);

    if (isDateBefore(newDate, minimumDate)) return;
    if (maxDate != null && isDateAfter(newDate, maxDate)) return;
    if (isDateAllowed && !isDateAllowed(newDate)) return;

    applyDate(newDate);
  };

  const handleManualInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (!newValue) {
      onChange('');
      setIsInvalidFormat(false);
      setHasError(false);
      onValidationChange?.(true);
      return;
    }

    if (newValue.length === 10) {
      if (!isValidDateFormat(newValue)) {
        setIsInvalidFormat(true);
        setHasError(true);
        onValidationChange?.(false);
        return;
      }

      const parsedDate = parseDate(newValue);
      if (!parsedDate) {
        setIsInvalidFormat(true);
        setHasError(true);
        onValidationChange?.(false);
        return;
      }

      if (isDateBefore(parsedDate, minimumDate)) {
        setIsInvalidFormat(false);
        setHasError(true);
        onChange(newValue);
        onValidationChange?.(false);
        return;
      }
      if (maxDate != null && isDateAfter(parsedDate, maxDate)) {
        setIsInvalidFormat(false);
        setHasError(true);
        onChange(newValue);
        onValidationChange?.(false);
        return;
      }
      if (isDateAllowed && !isDateAllowed(parsedDate)) {
        setIsInvalidFormat(false);
        setHasError(true);
        onChange(newValue);
        onValidationChange?.(false);
        return;
      }

      setIsInvalidFormat(false);
      setHasError(false);
      setSelectedDate(parsedDate);
      setTempSelectedDate(parsedDate);
      setCurrentMonth(parsedDate);
      onChange(newValue);
      onValidationChange?.(true);
    }
  };

  const isDateSelected = (day: number) => {
    if (!tempSelectedDate) return false;
    return (
      tempSelectedDate.getDate() === day &&
      tempSelectedDate.getMonth() === currentMonth.getMonth() &&
      tempSelectedDate.getFullYear() === currentMonth.getFullYear()
    );
  };

  const getCalendarDayMeta = (day: number, isCurrentMonth: boolean) => {
    if (!isCurrentMonth) {
      return { disabled: true, outOfSeason: false };
    }

    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (isDateBefore(date, minimumDate)) {
      return { disabled: true, outOfSeason: false };
    }
    if (maxDate != null && isDateAfter(date, maxDate)) {
      return { disabled: true, outOfSeason: false };
    }
    if (isDateAllowed && !isDateAllowed(date)) {
      return { disabled: true, outOfSeason: true };
    }
    return { disabled: false, outOfSeason: false };
  };

  const days = getDaysInMonth(currentMonth);
  const minDateFormatted = formatDate(minimumDate);

  const seasonFooterText = useMemo(() => {
    if (viewMode !== 'month' || !getSeasonCalendarFooterText) return null;
    return getSeasonCalendarFooterText(currentMonth.getFullYear(), currentMonth.getMonth());
  }, [viewMode, getSeasonCalendarFooterText, currentMonth]);

  const inputId = id ?? fallbackInputId;

  const getErrorMessage = () => {
    if (isInvalidFormat) {
      return 'Please enter a valid date in YYYY-MM-DD format.';
    }
    if (!hasError) return '';

    const parsed = parseDate(inputValue || value || '');
    if (parsed) {
      if (isDateBefore(parsed, minimumDate)) {
        if (maxDate != null) {
          return `Please choose a date between ${minDateFormatted} and ${formatDate(maxDate)}.`;
        }
        return `Please choose a start date on or after ${minDateFormatted}.`;
      }
      if (maxDate != null && isDateAfter(parsed, maxDate)) {
        return `Please choose a date between ${minDateFormatted} and ${formatDate(maxDate)}.`;
      }
      if (isDateAllowed && !isDateAllowed(parsed)) {
        return disallowedSelectionMessage ?? 'This date is not available.';
      }
    }
    if (maxDate != null) {
      return `Please choose a date between ${minDateFormatted} and ${formatDate(maxDate)}.`;
    }
    return `Please choose a start date on or after ${minDateFormatted}.`;
  };

  const calendarContent = (
    <div className={styles.calendarContent}>
      {viewMode === 'month' ? (
        <>
          <div className={styles.header}>
            <button
              type="button"
              className={styles.navButton}
              onClick={handlePrevMonth}
              aria-label="Previous month"
            >
              <ChevronDownIcon className={styles.prevIcon} />
            </button>
            <button
              type="button"
              className={styles.monthYearButton}
              onClick={openYearView}
              aria-label="Select year"
            >
              {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </button>
            <button
              type="button"
              className={styles.navButton}
              onClick={handleNextMonth}
              aria-label="Next month"
            >
              <ChevronDownIcon className={styles.nextIcon} />
            </button>
          </div>

          <div className={styles.weekdays}>
            {WEEKDAYS.map((day) => (
              <div key={day} className={styles.weekday}>
                {day}
              </div>
            ))}
          </div>

          <div
            className={styles.daysGrid}
          >
            {days.map((day, index) => {
              const meta = getCalendarDayMeta(day.date, day.isCurrentMonth);
              const dayClasses = classNames(
                styles.day,
                !day.isCurrentMonth && styles.otherMonth,
                isDateSelected(day.date) &&
                  day.isCurrentMonth &&
                  !meta.outOfSeason &&
                  styles.selected,
                meta.outOfSeason && styles.outOfSeason,
                meta.disabled && !meta.outOfSeason && day.isCurrentMonth && styles.disabled
              );

              return (
                <button
                  key={index}
                  type="button"
                  className={dayClasses}
                  onClick={() => {
                    if (day.isCurrentMonth && !meta.disabled) {
                      handleDateClick(day.date);
                    }
                  }}
                  disabled={meta.disabled}
                >
                  {day.date}
                </button>
              );
            })}
          </div>

          <div
            className={classNames(
              styles.seasonFooterSlot,
              seasonFooterText && styles.seasonFooterSlotVisible
            )}
            aria-hidden={!seasonFooterText}
          >
            <p className={styles.seasonFooter}>{seasonFooterText ?? '\u00a0'}</p>
          </div>
        </>
      ) : (
        <>
          <div className={styles.header}>
            <button
              type="button"
              className={styles.navButton}
              onClick={handlePrevYearChunk}
              aria-label="Previous years"
              disabled={yearChunkStart <= minYear}
            >
              <ChevronDownIcon className={styles.prevIcon} />
            </button>
            <button
              type="button"
              className={styles.monthYearButton}
              onClick={() => {
                setViewMode('month');
              }}
              aria-label="Back to month"
            >
              {firstYearInChunk != null && lastYearInChunk != null
                ? `${String(firstYearInChunk)} – ${String(lastYearInChunk)}`
                : ''}
            </button>
            <button
              type="button"
              className={styles.navButton}
              onClick={handleNextYearChunk}
              aria-label="Next years"
              disabled={lastYearInChunk == null || lastYearInChunk >= maxYear}
            >
              <ChevronDownIcon className={styles.nextIcon} />
            </button>
          </div>

          <div className={styles.yearsGrid}>
            {yearChunk.map((year) => (
              <button
                key={year}
                type="button"
                className={classNames(
                  styles.yearButton,
                  year === currentMonth.getFullYear() && styles.yearSelected
                )}
                onClick={() => {
                  handleYearSelect(year);
                }}
              >
                {year}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  const validationError =
    hasError || isInvalidFormat ? getErrorMessage() : undefined;

  const closePicker = () => {
    setTempSelectedDate(selectedDate);
    setViewMode('month');
    setIsOpen(false);
  };

  const handleMobileInputClick = () => {
    if (!isMobile) return;
    setIsOpen(true);
  };

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (isMobile) {
      // Keep mobile readonly input unfocused to avoid Android selection/caret UI.
      event.target.blur();
      return;
    }
    setIsOpen(true);
  };

  const handleContainerPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;

    // On mobile, avoid pointer-down toggles to prevent open/close races with sheet overlay clicks.
    if (isMobile) {
      return;
    }

    const target = event.target as Node;
    const clickedInsideInput = inputAreaRef.current?.contains(target) ?? false;
    const clickedInsideCalendar = calendarPanelRef.current?.contains(target) ?? false;

    if (clickedInsideCalendar) {
      return;
    }

    if (clickedInsideInput) {
      if (isOpen) {
        // Prevent the input focus event from reopening immediately.
        event.preventDefault();
        closePicker();
      } else {
        setIsOpen(true);
      }
      return;
    }

    if (isOpen) {
      closePicker();
    } else {
      setIsOpen(true);
    }
  };

  return (
    <div
      className={styles.datePicker}
      ref={dropdownRef}
      onPointerDown={handleContainerPointerDown}
    >
      <div ref={inputAreaRef} onClick={handleMobileInputClick}>
        <Input
          id={inputId}
          className={classNames(styles.field, className)}
          value={inputValue}
          onChange={handleManualInput}
          onFocus={handleInputFocus}
          placeholder="YYYY-MM-DD"
          maxLength={10}
          readOnly={isMobile}
          inputClassName={isMobile ? styles.inputReadOnlyTouch : undefined}
          error={validationError}
          rightIcon={<CalendarIcon className={styles.calendarGlyph} aria-hidden />}
        />
      </div>

      {isMobile && isOpen ? (
        <BottomSheet
          isOpen={isOpen}
          onClose={() => {
            closePicker();
          }}
          title={sheetTitle}
        >
          {calendarContent}
        </BottomSheet>
      ) : (
        isOpen && (
          <div
            ref={calendarPanelRef}
            className={classNames(
              styles.dropdown,
              dropdownPosition === 'above' && styles.dropdownAbove
            )}
          >
            {calendarContent}
          </div>
        )
      )}
    </div>
  );
};
